import { useEffect, useState } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import {
	hasUsercentricsServiceConsent,
	onUsercentricsConsentChanged,
	onUsercentricsReady,
	readStoredUsercentricsServiceConsent,
} from '@worldcoo/ui/lib/usercentrics';

/**
 * `'unknown'` is not `'denied'`: it is what the server renders while the CMP is asked asynchronously.
 * Collapsing the two makes a consented donor see a click-to-load prompt appear and then disappear.
 */
export type ServiceConsent = 'unknown' | 'granted' | 'denied';

/**
 * Starts `'unknown'` (or the mirror cookie's answer) on server and first client render alike, then
 * resolves in an effect from the stored record, then the CMP, and re-resolves on every change.
 */
export function useUsercentricsServiceConsent(serviceName: string): ServiceConsent {
	// An embed shows no banner (the integrator collected consent; see `isInIframe` in `__root.tsx`), so there
	// is no CMP to ask: the same premise `grantEmbeddedConsent` applies to Consent Mode storage.
	const { isInIframe, consentDecision } = useRouteContext({ from: '__root__' });
	// From the mirror cookie both sides read, so a donor who accepted gets the embed in the SSR markup.
	const [consent, setConsent] = useState<ServiceConsent>(() =>
		consentDecision?.granted.includes(serviceName)
			? 'granted'
			: consentDecision?.denied.includes(serviceName)
				? 'denied'
				: 'unknown',
	);

	useEffect(() => {
		if (isInIframe) return;
		let active = true;

		const settle = (granted: boolean) => {
			if (active) setConsent(granted ? 'granted' : 'denied');
		};

		const askCmp = () => {
			hasUsercentricsServiceConsent(serviceName)
				.then(settle)
				.catch((error: unknown) => {
					console.error(new Error(`Failed to read Usercentrics consent for "${serviceName}": ${String(error)}`));
					// Fail closed: a read we could not complete is not permission.
					settle(false);
				});
		};

		// The stored record is the same answer a second sooner. Not in the `useState` initialiser: the server has
		// no storage, so the first client render would disagree with the markup (React #418).
		const stored = readStoredUsercentricsServiceConsent(serviceName);
		if (stored !== undefined) settle(stored);

		// A returning donor's answer arrives only here: `hasUsercentricsServiceConsent` is `false` while the script
		// loads, and the decision the CMP restores at init fires no `UC_CONSENT_CHANGED`.
		const stopWaiting = onUsercentricsReady((available) => {
			if (available) askCmp();
			// No CMP (blocked by an extension, offline): with no stored record either, fail closed.
			else if (stored === undefined) settle(false);
		});
		const unsubscribe = onUsercentricsConsentChanged(askCmp);

		return () => {
			active = false;
			stopWaiting();
			unsubscribe();
		};
	}, [serviceName, isInIframe]);

	return isInIframe ? 'granted' : consent;
}
